<template>
  <div class="platform-notice-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">PLATFORM INFORMATION</p>
        <h1>運営からのお知らせ</h1>
        <p>
          ここで出したお知らせは、<strong>契約しているすべての会社のダッシュボード</strong>に
          同じ内容で出ます。社内向けの掲示ではありません。
        </p>
      </div>
      <CButton
        color="secondary"
        variant="outline"
        :disabled="loading || saving"
        @click="load"
      >
        再読込
      </CButton>
    </header>

    <!--
      **掲載期限が必須である理由を、書く人に見せる。**
      画面へ直に書いていた頃、1年近く前の知らせが残り続けた。
    -->
    <CAlert color="info" class="notice-alert">
      掲載を終える日は<strong>必ず入れてください。</strong>
      終わりを決めないお知らせは、誰も消さないまま古くなります。
      ずっと出したいものは、期限を延ばして出し直してください。
    </CAlert>
    <CAlert v-if="successMessage" color="success" close-button @update:show="successMessage = ''">
      {{ successMessage }}
    </CAlert>
    <CAlert v-if="errorMessage" color="danger" close-button @update:show="errorMessage = ''">
      {{ errorMessage }}
    </CAlert>

    <CCard class="notice-card">
      <CCardHeader class="section-header">
        <div>
          <strong>新しいお知らせ</strong>
          <small>出した時点から、指定した日の終わりまで出ます。</small>
        </div>
      </CCardHeader>
      <CCardBody>
        <div class="notice-form">
          <label class="notice-form__field notice-form__field--kind">
            <span>種類</span>
            <select v-model="form.category" class="form-control">
              <option value="SYSTEM">運用の連絡</option>
              <option value="UPDATE">機能の変更</option>
            </select>
          </label>
          <label class="notice-form__field notice-form__field--body">
            <span>内容（120文字まで）</span>
            <input
              v-model.trim="form.body"
              type="text"
              class="form-control"
              maxlength="120"
              placeholder="9月1日の深夜にメンテナンスを行います。"
            >
          </label>
          <label class="notice-form__field notice-form__field--until">
            <span>掲載を終える日</span>
            <input v-model="form.expires_on" type="date" class="form-control">
          </label>
          <CButton
            color="primary"
            :disabled="saving || !canPublish"
            @click="publish"
          >
            出す
          </CButton>
        </div>
      </CCardBody>
    </CCard>

    <CCard class="notice-card">
      <CCardHeader class="section-header">
        <div>
          <strong>出したお知らせ</strong>
          <small>終わったものも残します。何をいつ出したか確かめられるように。</small>
        </div>
      </CCardHeader>
      <CCardBody>
        <div v-if="loading" class="loading-state"><CSpinner color="primary"/></div>
        <p v-else-if="notices.length === 0" class="empty-state">
          まだ1件も出していません。いまはどの会社のダッシュボードにも、
          お知らせのカードが出ていません。
        </p>
        <table v-else class="notice-table">
          <thead>
            <tr>
              <th>状態</th>
              <th>出した日</th>
              <th>種類</th>
              <th>内容</th>
              <th>終わり</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="notice in notices" :key="notice.id">
              <td>
                <CBadge :color="isLive(notice) ? 'success' : 'secondary'">
                  {{ isLive(notice) ? '掲載中' : '終了' }}
                </CBadge>
              </td>
              <td class="notice-table__date">{{ day(notice.publishedAt) }}</td>
              <td>{{ categoryLabel(notice.category) }}</td>
              <td>{{ notice.body }}</td>
              <td class="notice-table__date">{{ day(notice.expiresAt) }}</td>
              <td>
                <CButton
                  v-if="isLive(notice)"
                  size="sm"
                  color="danger"
                  variant="outline"
                  :disabled="saving"
                  @click="end(notice)"
                >
                  やめる
                </CButton>
              </td>
            </tr>
          </tbody>
        </table>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import moment from 'moment'
import platformNoticeApi from '@/services/platformNotices'

const emptyForm = () => ({
  category: 'SYSTEM',
  body: '',
  /* **既定を入れない。**終わりは、書く人が毎回決める。 */
  expires_on: ''
})

export default {
  name: 'PlatformNotices',
  data () {
    return {
      loading: false,
      saving: false,
      errorMessage: '',
      successMessage: '',
      notices: [],
      form: emptyForm()
    }
  },
  computed: {
    /** 3つとも埋まっていないと出せない。**期限も含めて。** */
    canPublish () {
      return Boolean(this.form.category)
        && Boolean(this.form.body)
        && Boolean(this.form.expires_on)
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      this.errorMessage = ''
      try {
        this.notices = await platformNoticeApi.list()
      } catch (error) {
        this.notices = []
        this.errorMessage = this.readMessage(error, 'お知らせを取得できませんでした。')
      } finally {
        this.loading = false
      }
    },

    async publish () {
      this.saving = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        await platformNoticeApi.publish(this.form)
        this.form = emptyForm()
        this.successMessage = 'お知らせを出しました。'
        await this.load()
      } catch (error) {
        this.errorMessage = this.readMessage(error, 'お知らせを出せませんでした。')
      } finally {
        this.saving = false
      }
    },

    async end (notice) {
      this.saving = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        await platformNoticeApi.end(notice.id)
        this.successMessage = 'お知らせを終えました。'
        await this.load()
      } catch (error) {
        this.errorMessage = this.readMessage(error, 'お知らせを終えられませんでした。')
      } finally {
        this.saving = false
      }
    },

    /** いま出ているか。**終わりが未来にあるかどうかで見る。** */
    isLive (notice) {
      const until = moment(notice && notice.expiresAt)
      return until.isValid() && until.isAfter(moment())
    },

    day (value) {
      const at = moment(value)
      return at.isValid() ? at.format('YYYY.MM.DD') : ''
    },

    categoryLabel (category) {
      return category === 'UPDATE' ? '機能の変更' : '運用の連絡'
    },

    /**
     * サーバの言い分だけを出す。
     *
     * axios が付ける英語（Network Error など）は画面へ出さない。
     */
    readMessage (error, fallback) {
      const data = error && error.response && error.response.data
      if (data && typeof data.message === 'string' && data.message) {
        return data.message
      }
      return fallback
    }
  }
}
</script>

<style scoped>
.platform-notice-page { padding: 8px 0 32px; }

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}
.page-header h1 { margin: 0 0 8px; font-size: 24px; }
.page-header p { margin: 0; color: #6f8190; font-size: 13px; max-width: 60ch; }
.eyebrow {
  margin: 0 0 4px;
  color: var(--preika-primary, #1f6fb2);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .16em;
}

.notice-alert { font-size: 13px; }
.notice-card { margin-bottom: 20px; }

.section-header strong { display: block; }
.section-header small { color: #8798a5; }

.notice-form {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
}
.notice-form__field { display: flex; flex-direction: column; gap: 4px; margin: 0; }
.notice-form__field span { color: #6f8190; font-size: 12px; }
.notice-form__field--kind { width: 160px; }
.notice-form__field--body { flex: 1 1 320px; min-width: 240px; }
.notice-form__field--until { width: 180px; }

.loading-state, .empty-state { padding: 12px 0; color: #6f8190; font-size: 13px; }

/* 幅の広い表は、表の中だけで横に流す。**本文ごと横スクロールさせない。** */
.notice-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.notice-table th, .notice-table td {
  padding: 10px 8px;
  border-bottom: 1px solid #edf2f6;
  text-align: left;
  vertical-align: top;
}
.notice-table th { color: #8798a5; font-size: 11px; font-weight: 700; }
.notice-table__date { white-space: nowrap; font-variant-numeric: tabular-nums; }

@media (max-width: 767px) {
  .page-header { flex-direction: column; }
  .notice-form__field--kind,
  .notice-form__field--until { width: 100%; }
}
</style>
